/**
 * Upload tracking store — maps a freshly-uploaded image URL to its sha256
 * hash so any rendering surface can ask "what moderation status does this
 * URL have?" before the entity (proposal, market, profile) is persisted.
 *
 * The viewer-side JOIN against `image_uploads.nsfw_status` only kicks in
 * after the entity has been published. Within the proposal-creation
 * wizard, the URL exists but the entity does not yet — so we keep a
 * tab-scoped (sessionStorage) map of `url -> sha256` populated by
 * `<CropModal>` on upload success. `useNsfwStatusByUrl(url)`
 * reads this and polls `/v1/images/{sha256}/moderation-status`.
 *
 * Bounded at 50 entries (LRU). One creator session won't exceed this.
 */

import { createJSONStorage, persist } from 'zustand/middleware';

import { createTrackedStore } from '@/lib/dev/zustand-registry';

const MAX_ENTRIES = 50;
const STORAGE_KEY = 'morfi-upload-tracking';

type ModerationStatus = 'pending' | 'clean' | 'flagged' | 'minors_takedown' | 'failed';

export interface UploadTrackingState {
  /** Insertion-ordered map; oldest entry is evicted at MAX_ENTRIES. */
  urlToSha: Map<string, string>;
  /** Optional known verdicts for trusted server-generated URLs. */
  urlToStatus: Map<string, ModerationStatus>;
  /** Stash a freshly-uploaded URL alongside its sha256. */
  track: (url: string, sha256: string, status?: ModerationStatus) => void;
  /** Lookup sha256 by URL; undefined if not in the map. */
  getSha256: (url: string) => string | undefined;
  /** Lookup a known verdict by URL; undefined if it must be polled. */
  getStatus: (url: string) => ModerationStatus | undefined;
}

interface PersistedState {
  entries: Array<[string, string]>;
  statusEntries?: Array<[string, ModerationStatus]>;
}

// biome-ignore lint/nursery/useExplicitType: REASON: zustand's create + persist returns a complex generic UseBoundStore<...> chain that is more readable inferred.
export const useUploadTrackingStore = createTrackedStore<UploadTrackingState>(
  'useUploadTrackingStore',
)(
  persist(
    (set: (next: Partial<UploadTrackingState>) => void, get: () => UploadTrackingState) => ({
      urlToSha: new Map<string, string>(),
      urlToStatus: new Map<string, ModerationStatus>(),
      track(url: string, sha256: string, status?: ModerationStatus): void {
        if (typeof url !== 'string' || url.length === 0) return;
        if (typeof sha256 !== 'string' || sha256.length === 0) return;
        const nextSha = new Map(get().urlToSha);
        const nextStatus = new Map(get().urlToStatus);
        nextSha.delete(url);
        nextStatus.delete(url);
        nextSha.set(url, sha256);
        if (status !== undefined) nextStatus.set(url, status);
        while (nextSha.size > MAX_ENTRIES) {
          const firstKey = nextSha.keys().next().value;
          if (firstKey === undefined) break;
          nextSha.delete(firstKey);
          nextStatus.delete(firstKey);
        }
        set({ urlToSha: nextSha, urlToStatus: nextStatus });
      },
      getSha256(url: string): string | undefined {
        return get().urlToSha.get(url);
      },
      getStatus(url: string): ModerationStatus | undefined {
        return get().urlToStatus.get(url);
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state: UploadTrackingState): PersistedState => ({
        entries: Array.from(state.urlToSha.entries()),
        statusEntries: Array.from(state.urlToStatus.entries()),
      }),
      // ALLOW-UNKNOWN: pass-through type for opaque test/store JSON
      merge: (persisted: unknown, current: UploadTrackingState): UploadTrackingState => {
        const persistedShape = persisted as PersistedState | undefined;
        const entries = persistedShape?.entries ?? [];
        const statusEntries = persistedShape?.statusEntries ?? [];
        return {
          ...current,
          urlToSha: new Map(entries),
          urlToStatus: new Map(statusEntries),
        };
      },
    },
  ),
);
